import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, VStack, Heading, Text, Button, RadioGroup, Radio, Stack, Alert, AlertIcon, Spinner, Box, HStack, Badge } from '@chakra-ui/react';
import DOMPurify from 'dompurify';
import { api } from '../lib/api';

interface QData {
  id: number; text: string;
  options: { key: 'A'|'B'|'C'|'D'|'E'; text: string }[];
  correctAnswer: 'A'|'B'|'C'|'D'|'E';
  explanation: string;
  topic: { id: number; name: string } | null;
}

export default function QuestionView() {
  const { id } = useParams<{ id: string }>();
  const [q, setQ] = useState<QData | null>(null);
  const [picked, setPicked] = useState<string>('');
  const [submitted, setSubmitted] = useState(false);
  const [servedAt, setServedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api.getQuestion(Number(id))
      .then((data: QData) => {
        setQ(data);
        setServedAt(Date.now());
      })
      .catch(() => setError('Soru yüklenemedi.'));
  }, [id]);

  // (Audit Content-C1) Submit'te attempt'i Algomed backend'ine yaz —
  // mentor analytics + peer compare + stats dolu kalsin.
  const handleSubmit = () => {
    if (!q || !picked) return;
    setSubmitted(true);
    const isCorrect = picked === q.correctAnswer;
    const timeTaken = servedAt ? Math.max(0, Math.round((Date.now() - servedAt) / 1000)) : null;
    void api.recordAttempt({
      questionId: q.id,
      selectedAnswer: picked,
      isCorrect,
      timeTaken,
    }).catch((err: any) => {
      // 401/403 sustur — kullanici akisi kesilmesin
      if (err?.status !== 401 && err?.status !== 403) {
        // eslint-disable-next-line no-console
        console.warn('recordAttempt failed:', err?.message);
      }
    });
  };

  if (error) return <Container maxW="md" py={6}><Alert status="warning"><AlertIcon />{error}</Alert></Container>;
  if (!q) return <VStack p={12}><Spinner /></VStack>;

  const correct = submitted && picked === q.correctAnswer;
  return (
    <Container maxW="md" py={6}>
      <VStack spacing={4} align="stretch">
        {q.topic && <HStack><Badge colorScheme="blue">{q.topic.name}</Badge></HStack>}
        <Heading size="md" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(q.text) }} />

        <RadioGroup value={picked} onChange={setPicked} isDisabled={submitted}>
          <Stack>
            {q.options.map(o => {
              const isCorrect = submitted && o.key === q.correctAnswer;
              const isWrongPicked = submitted && o.key === picked && picked !== q.correctAnswer;
              return (
                <Box
                  key={o.key} p={3} borderRadius="md" border="1px solid"
                  borderColor={isCorrect ? 'optionCorrectBorderColor' : isWrongPicked ? 'optionIncorrectBorderColor' : 'optionDefaultBorderColor'}
                  bg={isCorrect ? 'optionCorrectBg' : isWrongPicked ? 'optionIncorrectBg' : 'optionDefaultBg'}
                  color={isCorrect || isWrongPicked ? 'white' : undefined}
                >
                  <Radio value={o.key}>
                    <Text as="span" fontWeight="bold" mr={2}>{o.key})</Text>
                    <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(o.text) }} />
                  </Radio>
                </Box>
              );
            })}
          </Stack>
        </RadioGroup>

        {!submitted ? (
          <Button colorScheme="brand" isDisabled={!picked} onClick={handleSubmit}>
            Cevapla
          </Button>
        ) : (
          <>
            <Alert status={correct ? 'success' : 'error'} borderRadius="md">
              <AlertIcon />
              {correct ? 'Doğru!' : `Doğru cevap: ${q.correctAnswer}`}
            </Alert>
            <Box
              bg="bgExplanation" border="1px solid" borderColor="borderExplanation"
              p={3} borderRadius="md"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(q.explanation) }}
            />
          </>
        )}
      </VStack>
    </Container>
  );
}
